<script setup lang="ts">
import { Head, Link, router, useForm, usePage } from '@inertiajs/vue3'
import { ref, computed, watch } from 'vue'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import ImageGallery from '@/Components/ImageGallery.vue'
import ModelViewer from '@/Components/ModelViewer.vue'
import ProductCard from '@/Components/ProductCard.vue'
import { Button, Input, Card, CardHeader, CardTitle, CardContent, PhoneInput, EmailInput } from '@/Components/ui'
import { useTranslate } from '@/Composables/useTranslate'

interface Variant {
    id: number
    label: string
    size: string | null
    max_height_cm: number | null
    color: string | null
    color_hex: string | null
    price_cents: number | null
    stock: number
    images?: Array<{ id: number; name: string; thumb: string; large: string; original: string }>
    model_url?: string | null
}

interface UpsellVariant {
    id: number
    label: string | null
    size: string | null
    color: string | null
    color_hex: string | null
    price_cents: number
}

interface Upsell {
    id: number
    slug: string
    name: Record<string, string>
    short_description?: Record<string, string>
    price_cents: number
    has_variants?: boolean
    currency: string
    thumb: string | null
    image: string | null
    is_purchasable: boolean
    variants: UpsellVariant[]
}

interface Product {
    id: number
    slug: string
    sku: string | null
    name: Record<string, string>
    short_description?: Record<string, string>
    description?: Record<string, string>
    specs?: Record<string, string> | null
    product_type: string
    frame_style: string | null
    variants: Variant[]
    price_cents: number
    sale_percent?: number | null
    is_highlighted?: boolean
    highlight_text?: Record<string, string> | null
    sale_price_cents?: number | null
    currency: string
    condition: 'new' | 'used'
    tax_mode: 'standard' | 'margin'
    tax_rate: number
    stock: number
    track_stock: boolean
    is_purchasable: boolean
    is_rentable: boolean
    is_leasable?: boolean
    rental_price_cents_per_day: number | null
    rental_deposit_cents: number | null
    rental_pricing_tiers?: Array<{ min_days: number; price_cents: number }>
    category: { id: number; slug: string; name: Record<string, string> } | null
    upsells?: Upsell[]
    images: Array<{ id: number; name: string; thumb: string; large: string; original: string }>
    model_url: string | null
    hotspots?: Array<{
        id: number
        variant_id?: number | null
        label: Record<string, string>
        description?: Record<string, string> | null
        position: string
        normal?: string | null
        camera_orbit?: string | null
        camera_target?: string | null
    }>
}

const props = defineProps<{
    product: Product
    related: Array<{ id: number; slug: string; name: Record<string, string>; price_cents: number; currency: string; thumb: string | null }>
    rental_window: { today: string; max_date: string }
    seo?: {
        title: string
        description: string
        og_image: string | null
        canonical: string
        json_ld: Record<string, any>
    }
}>()

const page = usePage()
const { locale, t } = useTranslate()

const tab = ref<'images' | '3d'>('images')
const showInquiry = ref(false)
const showTestRide = ref(false)
const showRental = ref(false)

const form = useForm({
    product_id: props.product.id,
    type: 'product',
    name: page.props.auth?.user?.name ?? '',
    email: page.props.auth?.user?.email ?? '',
    phone: '',
    message: '',
})

const testRideForm = useForm({
    product_id: props.product.id,
    customer_name: page.props.auth?.user?.name ?? '',
    email: page.props.auth?.user?.email ?? '',
    phone: '',
    preferred_at: '',
    customer_notes: '',
})

const rentalForm = useForm({
    start_date: '',
    end_date: '',
    customer_name: page.props.auth?.user?.name ?? '',
    email: page.props.auth?.user?.email ?? '',
    phone: '',
    customer_notes: '',
    accept_terms: false,
})

const rentalDays = computed(() => {
    if (!rentalForm.start_date || !rentalForm.end_date) return 0
    const s = new Date(rentalForm.start_date)
    const e = new Date(rentalForm.end_date)
    if (e < s) return 0
    return Math.round((e.getTime() - s.getTime()) / 86400000) + 1
})
const rentalRateCents = computed(() => {
    const days = rentalDays.value
    const base = props.product.rental_price_cents_per_day ?? 0
    if (days <= 0) return base
    const tiers = [...(props.product.rental_pricing_tiers ?? [])].sort((a, b) => a.min_days - b.min_days)
    let rate = base
    for (const t of tiers) {
        if (days >= t.min_days) rate = t.price_cents
    }
    return rate
})
const rentalTotal = computed(() => rentalDays.value * rentalRateCents.value)
const rentalSavings = computed(() => {
    const baseTotal = rentalDays.value * (props.product.rental_price_cents_per_day ?? 0)
    return Math.max(0, baseTotal - rentalTotal.value)
})
const nextTierHint = computed(() => {
    const tiers = [...(props.product.rental_pricing_tiers ?? [])].sort((a, b) => a.min_days - b.min_days)
    const days = rentalDays.value
    for (const t of tiers) if (days < t.min_days) return t
    return null
})

const name = computed(() => props.product.name[locale.value] ?? props.product.name.de ?? props.product.slug)
const desc = computed(() => props.product.description?.[locale.value] ?? props.product.description?.de ?? '')
const shortDesc = computed(() => props.product.short_description?.[locale.value] ?? props.product.short_description?.de ?? '')

const price = computed(() =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: props.product.currency }).format(effectivePriceCents.value / 100)
)
const showAbPrefix = computed(() => variants.value.length > 0 && !selectedVariant.value)
const hasSale = computed(() => !!props.product.sale_percent && !!props.product.sale_price_cents)
const salePrice = computed(() =>
    props.product.sale_price_cents
        ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: props.product.currency }).format(props.product.sale_price_cents / 100)
        : null
)

const rentalPrice = computed(() =>
    props.product.rental_price_cents_per_day !== null
        ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: props.product.currency }).format(props.product.rental_price_cents_per_day / 100)
        : null
)

const variants = computed(() => props.product.variants ?? [])
const isBike = computed(() => props.product.product_type === 'bike')

const specGroups = computed(() => [
    { title: t('spec.frame'), keys: [
        { key: 'frame_material', label: t('spec.frame_material') },
        { key: 'frame_height',   label: t('spec.frame_height') },
        { key: 'wheel_size',     label: t('spec.wheel_size') },
        { key: 'manufacturer_color', label: t('spec.manufacturer_color') },
    ]},
    { title: t('spec.suspension'), keys: [
        { key: 'fork',         label: t('spec.fork') },
        { key: 'travel_front', label: t('spec.travel_front') },
        { key: 'travel_rear',  label: t('spec.travel_rear') },
    ]},
    { title: t('spec.drivetrain'), keys: [
        { key: 'gears_count',     label: t('spec.gears_count') },
        { key: 'shift_type',      label: t('spec.shift_type') },
        { key: 'shifter',         label: t('spec.shifter') },
        { key: 'rear_derailleur', label: t('spec.rear_derailleur') },
        { key: 'front_derailleur',label: t('spec.front_derailleur') },
        { key: 'crankset',        label: t('spec.crankset') },
        { key: 'chain',           label: t('spec.chain') },
        { key: 'cassette',        label: t('spec.cassette') },
    ]},
    { title: t('spec.brakes'), keys: [
        { key: 'brake_type',       label: t('spec.brake_type') },
        { key: 'brake',            label: t('spec.brake') },
        { key: 'brake_disc_front', label: t('spec.brake_disc_front') },
        { key: 'brake_disc_rear',  label: t('spec.brake_disc_rear') },
    ]},
    { title: t('spec.wheels'), keys: [
        { key: 'wheelset',        label: t('spec.wheelset') },
        { key: 'rim',             label: t('spec.rim') },
        { key: 'hub_front',       label: t('spec.hub_front') },
        { key: 'hub_rear',        label: t('spec.hub_rear') },
        { key: 'tires',           label: t('spec.tires') },
        { key: 'tire_size_inch',  label: t('spec.tire_size_inch') },
        { key: 'tire_size_etrto', label: t('spec.tire_size_etrto') },
    ]},
    { title: t('spec.cockpit'), keys: [
        { key: 'handlebar', label: t('spec.handlebar') },
        { key: 'grips',     label: t('spec.grips') },
        { key: 'stem',      label: t('spec.stem') },
        { key: 'headset',   label: t('spec.headset') },
        { key: 'saddle',    label: t('spec.saddle') },
        { key: 'seatpost',  label: t('spec.seatpost') },
        { key: 'pedals',    label: t('spec.pedals') },
    ]},
    { title: t('spec.lighting'), keys: [
        { key: 'front_light', label: t('spec.front_light') },
        { key: 'rear_light',  label: t('spec.rear_light') },
        { key: 'dynamo',      label: t('spec.dynamo') },
        { key: 'kickstand',   label: t('spec.kickstand') },
        { key: 'fenders',     label: t('spec.fenders') },
        { key: 'rack',        label: t('spec.rack') },
    ]},
    { title: t('spec.dimensions'), keys: [
        { key: 'max_total_weight_kg', label: t('spec.max_total_weight_kg') },
        { key: 'weight_kg',           label: t('spec.weight_kg') },
    ]},
])

const filledSpecGroups = computed(() => {
    const specs = props.product.specs ?? {}
    const loc = locale.value
    return specGroups.value
        .map(g => ({
            title: g.title,
            rows: g.keys
                .map(k => {
                    const v = specs[k.key]
                    let value = ''
                    if (v && typeof v === 'object') {
                        value = ((v as any)[loc] || (v as any).de || '').trim()
                    } else if (typeof v === 'string') {
                        value = v.trim()
                    }
                    return { key: k.key, label: k.label, value }
                })
                .filter(r => r.value.length > 0),
        }))
        .filter(g => g.rows.length > 0)
})

const allColors = computed(() => {
    const seen = new Map<string, Variant>()
    variants.value.forEach(v => { if (v.color && !seen.has(v.color)) seen.set(v.color, v) })
    return Array.from(seen.values())
})
const allSizes = computed(() => {
    const seen = new Map<string, Variant>()
    variants.value.forEach(v => { if (v.size && !seen.has(v.size)) seen.set(v.size, v) })
    return Array.from(seen.values()).sort((a, b) => (a.max_height_cm ?? 999) - (b.max_height_cm ?? 999))
})

const selectedSize = ref<string | null>(allSizes.value[0]?.size ?? null)

const colorVariants = computed(() => {
    if (!selectedSize.value) return allColors.value
    const seen = new Map<string, Variant>()
    variants.value
        .filter(v => v.size === selectedSize.value)
        .forEach(v => { if (v.color && !seen.has(v.color)) seen.set(v.color, v) })
    return Array.from(seen.values())
})

const sizeVariants = computed(() => allSizes.value)

const selectedColor = ref<string | null>(colorVariants.value[0]?.color ?? null)

watch(selectedSize, () => {
    const colorsForSize = colorVariants.value.map(v => v.color)
    if (selectedColor.value && !colorsForSize.includes(selectedColor.value)) {
        selectedColor.value = colorsForSize[0] ?? null
    } else if (!selectedColor.value && colorsForSize.length) {
        selectedColor.value = colorsForSize[0]
    }
})

const selectedVariant = computed<Variant | null>(() => {
    if (!variants.value.length) return null
    return variants.value.find(v => (!selectedColor.value || v.color === selectedColor.value) && (!selectedSize.value || v.size === selectedSize.value)) ?? null
})

const effectivePriceCents = computed(() =>
    selectedVariant.value?.price_cents ?? props.product.sale_price_cents ?? props.product.price_cents
)

const inStock = computed(() => {
    if (selectedVariant.value) return selectedVariant.value.stock > 0
    return !props.product.track_stock || props.product.stock > 0
})

const displayStock = computed(() => selectedVariant.value?.stock ?? props.product.stock)

const displayImages = computed(() => {
    const v = selectedVariant.value
    if (v && v.images && v.images.length) return v.images
    return props.product.images
})

const displayModelUrl = computed(() => selectedVariant.value?.model_url ?? props.product.model_url)
const displayHotspots = computed(() => {
    const all = props.product.hotspots ?? []
    const vid = selectedVariant.value?.id
    if (!vid) return all.filter(h => !h.variant_id)
    const variantSpecific = all.filter(h => h.variant_id === vid)
    return variantSpecific.length ? variantSpecific : all.filter(h => !h.variant_id)
})

const showSizeFinder = ref(false)
const specsExpanded = ref(false)

function fmt2(cents: number, currency = 'EUR') {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(cents / 100)
}
const heightInput = ref<number | null>(null)
const recommendedSize = computed(() => {
    if (!heightInput.value) return null
    const sorted = [...sizeVariants.value].sort((a, b) => (a.max_height_cm ?? 999) - (b.max_height_cm ?? 999))
    return sorted.find(v => v.max_height_cm !== null && heightInput.value! <= v.max_height_cm)
        ?? sorted[sorted.length - 1] ?? null
})
function applyRecommendation() {
    if (recommendedSize.value?.size) {
        selectedSize.value = recommendedSize.value.size
        showSizeFinder.value = false
    }
}

function submitInquiry() {
    form.post('/inquiries', {
        preserveScroll: true,
        onSuccess: () => {
            form.reset('message', 'phone')
            showInquiry.value = false
        },
    })
}

const adding = ref(false)
const showAddedModal = ref(false)
const upsellPicking = ref<number | null>(null)   // product-id whose variant picker is open
const upsellAdded = ref<Set<number>>(new Set())
const upsellAdding = ref<number | null>(null)    // variant-id (or product-id for no-variant) being added

function addToCart() {
    adding.value = true
    router.post('/cart', { product_id: props.product.id, variant_id: selectedVariant.value?.id ?? null, quantity: 1 }, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => { showAddedModal.value = true },
        onError: () => { adding.value = false },
        onFinish: () => { adding.value = false },
    })
}

function buyNow() {
    adding.value = true
    router.post('/cart', { product_id: props.product.id, variant_id: selectedVariant.value?.id ?? null, quantity: 1 }, {
        preserveScroll: true,
        onSuccess: () => { router.visit('/checkout') },
        onError: () => { adding.value = false },
    })
}

function addUpsell(productId: number, variantId?: number) {
    upsellAdding.value = variantId ?? productId
    router.post('/cart', { product_id: productId, variant_id: variantId ?? null, quantity: 1 }, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            upsellAdded.value = new Set([...upsellAdded.value, productId])
            upsellPicking.value = null
        },
        onFinish: () => { upsellAdding.value = null },
    })
}

function submitTestRide() {
    testRideForm.post('/probefahrt', {
        preserveScroll: true,
        onSuccess: () => { showTestRide.value = false; testRideForm.reset('customer_notes', 'phone', 'preferred_at') },
    })
}

function submitRental() {
    rentalForm.post(`/verleih/${props.product.slug}`)
}
</script>

<template>
    <Head :title="seo?.title || name">
        <meta v-if="seo?.description" name="description" :content="seo.description" />
        <meta property="og:title" :content="seo?.title || name" />
        <meta v-if="seo?.description" property="og:description" :content="seo.description" />
        <meta v-if="seo?.og_image" property="og:image" :content="seo.og_image" />
        <meta v-if="seo?.canonical" property="og:url" :content="seo.canonical" />
        <meta property="og:type" content="product" />
        <meta name="twitter:card" content="summary_large_image" />
        <link v-if="seo?.canonical" rel="canonical" :href="seo.canonical" />
        <component :is="'script'" v-if="seo?.json_ld" type="application/ld+json" v-html="JSON.stringify(seo.json_ld)" />
    </Head>
    <PublicLayout>
        <article class="px-6 py-10 lg:px-12">
            <nav class="mb-4 text-xs text-gray-500">
                <a href="/shop" class="hover:underline">{{ t('Shop') }}</a>
                <template v-if="product.category">
                    <span class="mx-1">/</span>
                    <a :href="`/shop?category=${product.category.slug}`" class="hover:underline">
                        {{ product.category.name[locale] ?? product.category.name.de }}
                    </a>
                </template>
            </nav>

            <div class="grid gap-10 lg:grid-cols-2">
                <div>
                    <div class="relative">
                        <ImageGallery :images="displayImages" />
                        <button
                            v-if="displayModelUrl"
                            @click="tab = '3d'"
                            class="absolute right-3 top-3 z-10 inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-lg ring-2 ring-white/20 transition hover:bg-brand-700 hover:scale-105"
                        >
                            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                                <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"/>
                            </svg>
                            <span class="hidden sm:inline">{{ t('View in 3D') }}</span>
                            <span class="sm:hidden font-bold">3D</span>
                        </button>
                    </div>
                </div>

                <div>
                    <span v-if="product.condition === 'used'" class="inline-block rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">gebraucht</span>
                    <!-- Highlight-Badge -->
                    <span v-if="product.is_highlighted && product.highlight_text" class="group relative ml-1 inline-flex cursor-default items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        ★ {{ product.highlight_text?.[locale] || product.highlight_text?.de }}
                        <span class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 hidden w-56 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-xl group-hover:block dark:bg-gray-100 dark:text-gray-900">
                            {{ product.highlight_text?.de }}
                            <template v-if="product.highlight_text?.en && product.highlight_text.en !== product.highlight_text.de">
                                <br /><span class="opacity-70">{{ product.highlight_text.en }}</span>
                            </template>
                        </span>
                    </span>
                    <h1 class="mt-2 text-3xl font-bold">{{ name }}</h1>
                    <p v-if="shortDesc" class="mt-2 text-gray-600 dark:text-gray-400">{{ shortDesc }}</p>

                    <div class="mt-6">
                        <div v-if="hasSale" class="flex items-baseline gap-3">
                            <span class="text-3xl font-bold text-red-600">{{ salePrice }}</span>
                            <span class="text-xl text-gray-500 line-through">{{ price }}</span>
                            <span class="rounded bg-red-600 px-2 py-0.5 text-sm font-bold text-white">−{{ product.sale_percent }}%</span>
                        </div>
                        <p v-else class="text-3xl font-bold text-brand-600">
                            <span v-if="showAbPrefix" class="text-lg font-normal text-gray-500">ab </span>{{ price }}
                        </p>
                        <p class="text-xs text-gray-500">
                            {{ product.tax_mode === 'margin' ? t('VAT margin scheme §25a UStG, no VAT shown') : t('incl. :rate% VAT', { rate: product.tax_rate }) }}
                        </p>
                        <p v-if="product.is_rentable && rentalPrice" class="mt-2 text-sm">
                            <span class="font-medium">{{ t('Rental') }}:</span> {{ t('Rental: :price / day', { price: rentalPrice }) }}
                        </p>
                    </div>

                    <div v-if="colorVariants.length" class="mt-6">
                        <label class="mb-2 block text-sm font-medium">{{ t('Farbe') }}: <span class="text-gray-500">{{ selectedColor }}</span></label>
                        <div class="flex flex-wrap gap-2">
                            <button
                                v-for="v in colorVariants" :key="v.color"
                                type="button"
                                @click="selectedColor = v.color"
                                :title="v.color || ''"
                                :class="['h-10 w-10 rounded-full border-2 transition', selectedColor === v.color ? 'border-brand-600 ring-2 ring-brand-300' : 'border-gray-300 hover:border-gray-500']"
                                :style="{ backgroundColor: v.color_hex || '#9ca3af' }"
                            />
                        </div>
                    </div>

                    <div v-if="sizeVariants.length" class="mt-5">
                        <div class="mb-2">
                            <label class="text-sm font-medium">{{ t('Größe') }}: <span class="text-gray-500">{{ selectedSize }}</span></label>
                        </div>
                        <div class="flex flex-wrap items-center gap-2">
                            <button
                                v-for="v in sizeVariants" :key="v.size"
                                type="button"
                                @click="selectedSize = v.size"
                                :class="['rounded-md border px-4 py-2 text-sm font-medium transition', selectedSize === v.size ? 'border-brand-600 bg-brand-600 text-white' : 'border-gray-300 hover:border-gray-500 dark:border-gray-700']"
                            >
                                {{ v.size }}
                                <span v-if="v.max_height_cm" class="ml-1 text-[10px] opacity-70">≤{{ v.max_height_cm }}cm</span>
                            </button>
                            <button
                                type="button"
                                @click="showSizeFinder = true"
                                class="ml-1 inline-flex items-center gap-1.5 rounded-md border-2 border-dashed border-brand-400 px-3 py-2 text-sm font-semibold text-brand-700 transition hover:border-brand-600 hover:bg-brand-50 dark:border-brand-500 dark:text-brand-300 dark:hover:bg-brand-900/30"
                            >
                                📐 {{ t('Größenfinder') }}
                            </button>
                        </div>
                    </div>

                    <div class="mt-6 space-y-2">
                        <div v-if="product.is_purchasable" class="grid gap-2 sm:grid-cols-2">
                            <Button size="lg" :disabled="adding" @click="buyNow">
                                {{ t('Direkt kaufen') }}
                            </Button>
                            <Button size="lg" variant="outline" :disabled="adding" @click="addToCart">
                                🛒 {{ t('In den Warenkorb') }}
                            </Button>
                        </div>
                        <div class="grid gap-2 sm:grid-cols-2">
                            <Button v-if="isBike" size="lg" variant="outline" class="w-full" @click="showTestRide = true">{{ t('Probefahrt buchen') }}</Button>
                            <Button v-if="product.is_rentable && product.rental_price_cents_per_day" size="lg" variant="outline" class="w-full" @click="showRental = true">{{ t('Leihen') }}</Button>
                            <Link v-if="product.is_leasable" :href="`/leasing/anfrage?product=${product.id}`">
                                <Button size="lg" variant="outline" class="w-full">💼 {{ t('Leasing / Dienstrad') }}</Button>
                            </Link>
                            <Button v-if="!product.is_purchasable" size="lg" class="w-full" @click="showInquiry = true">{{ t('Anfrage stellen') }}</Button>
                        </div>
                    </div>
                    <button v-if="product.is_purchasable" type="button" @click="showInquiry = !showInquiry" class="mt-2 text-sm text-gray-500 hover:underline">{{ t('Lieber Frage stellen?') }}</button>

                    <p v-if="product.is_purchasable && product.track_stock" class="mt-2 text-sm">
                        <span v-if="displayStock === 0" class="text-blue-600 dark:text-blue-400">● {{ t('Auf Bestellung — wir besorgen es dir') }}</span>
                        <span v-else-if="displayStock < 3" class="text-amber-600">● {{ t('Nur noch :count verfügbar', { count: displayStock }) }}</span>
                        <span v-else class="text-emerald-600">● {{ t('Auf Lager') }}</span>
                    </p>

                    <Card v-if="showInquiry" class="mt-6">
                        <CardHeader><CardTitle>{{ t('Anfrage zum Produkt') }}</CardTitle></CardHeader>
                        <CardContent>
                            <p v-if="(page.props.flash as any)?.success" class="mb-4 rounded bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                {{ (page.props.flash as any).success }}
                            </p>
                            <form @submit.prevent="submitInquiry" class="space-y-3">
                                <div class="grid gap-3 md:grid-cols-2">
                                    <div>
                                        <label class="mb-1 block text-xs font-medium">{{ t('Name') }} *</label>
                                        <Input v-model="form.name" required />
                                    </div>
                                    <div>
                                        <label class="mb-1 block text-xs font-medium">{{ t('E-Mail') }} *</label>
                                        <EmailInput v-model="form.email" required />
                                    </div>
                                </div>
                                <div>
                                    <label class="mb-1 block text-xs font-medium">{{ t('Telefon (optional)') }}</label>
                                    <PhoneInput v-model="form.phone" />
                                </div>
                                <div>
                                    <label class="mb-1 block text-xs font-medium">{{ t('Nachricht') }} *</label>
                                    <textarea
                                        v-model="form.message"
                                        rows="4"
                                        required
                                        class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                                    />
                                </div>
                                <Button type="submit" :disabled="form.processing">{{ t('Anfrage senden') }}</Button>
                            </form>
                        </CardContent>
                    </Card>

                    <div v-if="desc" class="prose mt-8 max-w-none dark:prose-invert" v-html="desc"></div>

                    <div class="mt-6 grid grid-cols-2 gap-2 text-sm">
                        <div class="text-gray-500">{{ t('SKU') }}</div><div>{{ product.sku ?? '-' }}</div>
                        <div class="text-gray-500">{{ t('Condition') }}</div><div>{{ product.condition === 'new' ? t('Neuware') : t('Gebraucht') }}</div>
                    </div>
                </div>
            </div>

            <section v-if="isBike && filledSpecGroups.length" class="mt-12">
                <h2 class="mb-4 text-2xl font-bold">{{ t('Spezifikationen') }}</h2>
                <div class="relative">
                    <div :class="['grid gap-6 md:grid-cols-2 transition-all', specsExpanded ? '' : 'max-h-[420px] overflow-hidden']">
                        <div v-for="(group, gi) in filledSpecGroups" :key="gi">
                            <h3 class="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">{{ group.title }}</h3>
                            <dl class="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-700">
                                <div v-for="row in group.rows" :key="row.key" class="flex justify-between gap-3 px-3 py-2 text-sm">
                                    <dt class="text-gray-500">{{ row.label }}</dt>
                                    <dd class="text-right">{{ row.value }}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                    <div v-if="!specsExpanded" class="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white to-transparent dark:from-gray-950"></div>
                </div>
                <div class="mt-4 text-center">
                    <button type="button" @click="specsExpanded = !specsExpanded" class="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800">
                        {{ specsExpanded ? t('Spezifikationen einklappen') : t('Alle Spezifikationen anzeigen') }}
                        <svg :class="['h-4 w-4 transition', specsExpanded ? 'rotate-180' : '']" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd"/></svg>
                    </button>
                </div>
            </section>

            <section v-if="related.length" class="mt-16">
                <h2 class="mb-4 text-xl font-bold">{{ t('Related products') }}</h2>
                <div class="grid gap-6 sm:grid-cols-2 md:grid-cols-4">
                    <ProductCard v-for="p in related" :key="p.id" :product="p as any" />
                </div>
            </section>
        </article>

        <Teleport to="body">
            <transition
                enter-active-class="transition ease-out duration-150"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-in duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="tab === '3d' && displayModelUrl"
                    class="fixed inset-0 z-[120] bg-gray-100 dark:bg-gray-950"
                >
                    <div class="absolute inset-0">
                        <ModelViewer
                            :key="`mv-${selectedVariant?.id ?? 'product'}`"
                            :src="displayModelUrl"
                            :alt="name"
                            :hotspots="displayHotspots"
                            :locale="locale"
                            class="h-full"
                        />
                    </div>
                    <div v-if="variants.length && (sizeVariants.length > 1 || colorVariants.length > 1)" class="absolute inset-x-3 bottom-16 z-30 flex flex-col items-start gap-2 sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-3 sm:-translate-x-1/2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 sm:rounded-full sm:bg-gray-900/85 sm:px-4 sm:py-2 sm:shadow-lg sm:backdrop-blur">
                        <div v-if="sizeVariants.length > 1" class="flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-gray-900/85 px-3 py-1.5 backdrop-blur sm:bg-transparent sm:px-0 sm:py-0">
                            <button
                                v-for="v in sizeVariants" :key="`mv-size-${v.size}`"
                                type="button"
                                @click="selectedSize = v.size"
                                :class="['shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition', selectedSize === v.size ? 'bg-white text-gray-900' : 'text-white/80 hover:bg-white/15']"
                            >
                                {{ v.size }}
                                <span v-if="v.max_height_cm" class="ml-1 text-[9px] opacity-70">≤{{ v.max_height_cm }}</span>
                            </button>
                        </div>
                        <div v-if="colorVariants.length > 1" class="flex max-w-full items-center gap-1.5 overflow-x-auto rounded-full bg-gray-900/85 px-3 py-1.5 backdrop-blur sm:bg-transparent sm:px-0 sm:py-0">
                            <span v-if="sizeVariants.length > 1" class="hidden sm:block h-4 w-px bg-white/20" />
                            <button
                                v-for="v in colorVariants" :key="`mv-color-${v.color}`"
                                type="button"
                                @click="selectedColor = v.color"
                                :title="v.color || ''"
                                :class="['shrink-0 h-7 w-7 rounded-full border-2 transition', selectedColor === v.color ? 'border-white ring-2 ring-brand-400' : 'border-white/30 hover:border-white/70']"
                                :style="{ backgroundColor: v.color_hex || '#9ca3af' }"
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        @click="tab = 'images'"
                        class="absolute right-3 top-3 z-30 inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-red-700"
                    >
                        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 18L18 6M6 6l12 12"/></svg>
                        {{ t('Exit 3D view') }}
                    </button>
                </div>
            </transition>
        </Teleport>

        <Teleport to="body">
            <div v-if="showTestRide" class="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 p-4" @click.self="showTestRide = false">
                <div class="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900">
                    <div class="mb-4 flex items-start justify-between">
                        <h3 class="text-lg font-bold">{{ t('Probefahrt buchen') }}</h3>
                        <button @click="showTestRide = false" class="text-gray-400 hover:text-gray-600">✕</button>
                    </div>
                    <p class="mb-4 text-sm text-gray-600 dark:text-gray-400">{{ name }}</p>
                    <form @submit.prevent="submitTestRide" class="space-y-3">
                        <div>
                            <label class="mb-1 block text-xs font-medium">{{ t('Wunschtermin *') }}</label>
                            <Input v-model="testRideForm.preferred_at" type="datetime-local" required />
                            <p v-if="testRideForm.errors.preferred_at" class="mt-1 text-xs text-red-600">{{ testRideForm.errors.preferred_at }}</p>
                        </div>
                        <div>
                            <label class="mb-1 block text-xs font-medium">{{ t('Name') }} *</label>
                            <Input v-model="testRideForm.customer_name" required />
                        </div>
                        <div class="grid gap-3 md:grid-cols-2">
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ t('E-Mail') }} *</label>
                                <EmailInput v-model="testRideForm.email" required />
                            </div>
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ t('Telefon') }}</label>
                                <PhoneInput v-model="testRideForm.phone" />
                            </div>
                        </div>
                        <div>
                            <label class="mb-1 block text-xs font-medium">{{ t('Anmerkung') }}</label>
                            <textarea v-model="testRideForm.customer_notes" rows="2" class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" />
                        </div>
                        <div class="flex gap-2 pt-2">
                            <Button type="button" variant="outline" class="flex-1" @click="showTestRide = false">{{ t('Cancel') }}</Button>
                            <Button type="submit" class="flex-1" :disabled="testRideForm.processing">{{ t('Submit') }}</Button>
                        </div>
                    </form>
                </div>
            </div>
        </Teleport>

        <Teleport to="body">
            <div v-if="showRental && product.is_rentable && product.rental_price_cents_per_day" class="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 p-4" @click.self="showRental = false">
                <div class="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900">
                    <div class="mb-4 flex items-start justify-between">
                        <h3 class="text-lg font-bold">{{ t('Leihen') }}</h3>
                        <button @click="showRental = false" class="text-gray-400 hover:text-gray-600">✕</button>
                    </div>
                    <p class="mb-1 text-sm text-gray-600 dark:text-gray-400">{{ name }}</p>
                    <p class="mb-4 text-xs text-gray-500">{{ t('Rental: :price / day', { price: rentalPrice ?? '' }) }}<span v-if="product.rental_deposit_cents"> · {{ t('Deposit :amount (in store)', { amount: new Intl.NumberFormat('de-DE', { style: 'currency', currency: product.currency }).format(product.rental_deposit_cents / 100) }) }}</span></p>
                    <form @submit.prevent="submitRental" class="space-y-3">
                        <div class="grid gap-3 md:grid-cols-2">
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ t('from') }} *</label>
                                <Input v-model="rentalForm.start_date" type="date" :min="rental_window.today" :max="rental_window.max_date" required />
                            </div>
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ t('to') }} *</label>
                                <Input v-model="rentalForm.end_date" type="date" :min="rentalForm.start_date || rental_window.today" :max="rental_window.max_date" required />
                            </div>
                        </div>
                        <div v-if="rentalDays > 0" class="space-y-1 text-sm">
                            <p>{{ rentalDays }} {{ rentalDays > 1 ? t('days') : t('day') }} × {{ fmt2(rentalRateCents, product.currency) }} = <strong>{{ fmt2(rentalTotal, product.currency) }}</strong></p>
                            <p v-if="rentalSavings > 0" class="text-xs text-emerald-600">💰 {{ t('Du sparst :amount durch Mengenrabatt.', { amount: fmt2(rentalSavings, product.currency) }) }}</p>
                            <p v-else-if="nextTierHint" class="text-xs text-gray-500">
                                {{ t('Tipp: ab :days Tagen nur :rate / Tag.', {
                                    days: nextTierHint.min_days,
                                    rate: fmt2(nextTierHint.price_cents, product.currency),
                                }) }}
                            </p>
                        </div>
                        <p v-if="rentalForm.errors.start_date" class="rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">{{ rentalForm.errors.start_date }}</p>
                        <div>
                            <label class="mb-1 block text-xs font-medium">{{ t('Name') }} *</label>
                            <Input v-model="rentalForm.customer_name" required />
                        </div>
                        <div class="grid gap-3 md:grid-cols-2">
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ t('E-Mail') }} *</label>
                                <EmailInput v-model="rentalForm.email" required />
                            </div>
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ t('Telefon') }}</label>
                                <PhoneInput v-model="rentalForm.phone" />
                            </div>
                        </div>
                        <label class="flex items-start gap-2 text-xs">
                            <input v-model="rentalForm.accept_terms" type="checkbox" required class="mt-0.5" />
                            <span>{{ t('I accept the') }} <a href="/agb" target="_blank" class="text-brand-600 hover:underline">{{ t('Terms') }}</a>. {{ t('Deposit at pickup in store.') }}</span>
                        </label>
                        <div class="flex gap-2 pt-2">
                            <Button type="button" variant="outline" class="flex-1" @click="showRental = false">{{ t('Cancel') }}</Button>
                            <Button type="submit" class="flex-1" :disabled="rentalForm.processing || rentalDays === 0 || !rentalForm.accept_terms">
                                {{ t('Book') }}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Teleport>

        <Teleport to="body">
            <div v-if="showSizeFinder" class="fixed inset-0 z-[140] flex items-center justify-center bg-black/60 p-4" @click.self="showSizeFinder = false">
                <div class="w-full max-w-md rounded-lg bg-white p-6 text-gray-900 shadow-xl dark:bg-gray-900 dark:text-gray-100">
                    <div class="mb-4 flex items-center justify-between">
                        <h3 class="text-lg font-bold">📐 {{ t('Größenfinder') }}</h3>
                        <button @click="showSizeFinder = false" class="text-gray-500 hover:text-gray-900 dark:hover:text-white">✕</button>
                    </div>
                    <p class="mb-4 text-sm text-gray-600 dark:text-gray-400">{{ t('Gib deine Körpergröße in cm ein, wir empfehlen die passende Rahmengröße.') }}</p>
                    <div class="space-y-3">
                        <div>
                            <label class="mb-1 block text-sm font-medium">{{ t('Körpergröße') }} (cm)</label>
                            <Input v-model="heightInput" type="number" min="120" max="220" placeholder="z.B. 178" />
                        </div>
                        <div v-if="recommendedSize" class="rounded-md border border-brand-200 bg-brand-50 p-4 text-sm dark:border-brand-800 dark:bg-brand-900/30">
                            <p class="text-gray-700 dark:text-gray-200">{{ t('Empfehlung') }}:</p>
                            <p class="mt-1 text-2xl font-bold text-brand-600 dark:text-brand-300">{{ recommendedSize.size }}</p>
                            <p v-if="recommendedSize.max_height_cm" class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">bis {{ recommendedSize.max_height_cm }} cm</p>
                        </div>
                        <div class="flex gap-2 pt-2">
                            <Button type="button" variant="outline" class="flex-1" @click="showSizeFinder = false">{{ t('Cancel') }}</Button>
                            <Button type="button" class="flex-1" :disabled="!recommendedSize" @click="applyRecommendation">{{ t('Größe übernehmen') }}</Button>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>

        <Teleport to="body">
            <div v-if="showAddedModal" class="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 p-4" @click.self="showAddedModal = false">
                <div class="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
                    <div class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-800">
                        <div class="flex items-center gap-2">
                            <span class="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">✓</span>
                            <h3 class="text-lg font-bold">{{ t('In den Warenkorb gelegt') }}</h3>
                        </div>
                        <button @click="showAddedModal = false" class="text-gray-500 hover:text-gray-900 dark:hover:text-white">✕</button>
                    </div>
                    <div class="px-6 py-5">
                        <p class="mb-1 text-sm text-gray-600 dark:text-gray-400">{{ name }}</p>
                        <p v-if="selectedVariant" class="text-xs text-gray-500">
                            <span v-if="selectedVariant.size">Größe {{ selectedVariant.size }}</span>
                            <span v-if="selectedVariant.color"> · {{ selectedVariant.color }}</span>
                        </p>

                        <div v-if="(product.upsells?.length ?? 0) > 0" class="mt-6">
                            <h4 class="mb-2 text-sm font-semibold">{{ t('Vergisst du nichts?') }}</h4>
                            <p class="mb-3 text-xs text-gray-500">{{ t('Mike empfiehlt dazu — vielleicht passt was davon zu deinem Bike.') }}</p>
                            <div class="grid gap-3 sm:grid-cols-3">
                                <div v-for="u in product.upsells" :key="u.id" class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950">
                                    <a :href="`/shop/${u.slug}`" class="block">
                                        <div class="aspect-square w-full overflow-hidden bg-gray-50 dark:bg-gray-900">
                                            <img v-if="u.image || u.thumb" :src="u.image || u.thumb || ''" class="h-full w-full object-contain" />
                                        </div>
                                    </a>
                                    <div class="p-3">
                                        <p class="line-clamp-2 text-xs font-medium">{{ u.name?.[locale] ?? u.name?.de ?? u.slug }}</p>
                                        <p class="mt-1 text-sm font-bold text-brand-600">
                                            <span v-if="u.has_variants" class="text-xs font-normal text-gray-500">ab </span>{{ fmt2(u.price_cents, u.currency) }}
                                        </p>
                                        <!-- Bereits hinzugefügt -->
                                        <div v-if="upsellAdded.has(u.id)" class="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md bg-emerald-50 px-2 py-1.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                            ✓ {{ t('Hinzugefügt') }}
                                        </div>
                                        <!-- Kein Varianten-Produkt: direkt hinzufügen -->
                                        <button v-else-if="u.is_purchasable && !u.has_variants" @click="addUpsell(u.id)" :disabled="upsellAdding !== null" class="mt-2 inline-flex w-full items-center justify-center rounded-md border border-brand-600 px-2 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-brand-300 dark:hover:bg-brand-900/30">
                                            + {{ t('Dazu') }}
                                        </button>
                                        <!-- Varianten-Produkt -->
                                        <div v-else-if="u.is_purchasable && u.has_variants" class="mt-2">
                                            <!-- Picker geschlossen -->
                                            <button v-if="upsellPicking !== u.id" @click="upsellPicking = u.id" class="inline-flex w-full items-center justify-center rounded-md border border-brand-600 px-2 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-900/30">
                                                + {{ t('Dazu') }}
                                            </button>
                                            <!-- Variant-Picker offen -->
                                            <div v-else class="space-y-1">
                                                <button
                                                    v-for="v in u.variants"
                                                    :key="v.id"
                                                    @click="addUpsell(u.id, v.id)"
                                                    :disabled="upsellAdding !== null"
                                                    class="flex w-full items-center gap-2 rounded border border-gray-200 px-2 py-1 text-xs hover:border-brand-500 hover:bg-brand-50 disabled:opacity-50 dark:border-gray-700 dark:hover:bg-brand-900/20"
                                                >
                                                    <span v-if="v.color_hex" class="inline-block h-3 w-3 shrink-0 rounded-full border border-gray-300" :style="{ background: v.color_hex }" />
                                                    <span class="truncate">{{ v.label || v.color || v.size || 'Variante' }}</span>
                                                    <span v-if="v.price_cents" class="ml-auto shrink-0 text-gray-500">{{ fmt2(v.price_cents, u.currency) }}</span>
                                                </button>
                                                <button @click="upsellPicking = null" class="w-full text-center text-xs text-gray-400 hover:text-gray-600">← {{ t('Zurück') }}</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-800 dark:bg-gray-950">
                        <button @click="showAddedModal = false" class="text-sm text-gray-600 hover:text-gray-900 hover:underline dark:text-gray-300">{{ t('Weiter stöbern') }}</button>
                        <a href="/checkout">
                            <Button>{{ t('Zum Checkout') }} →</Button>
                        </a>
                    </div>
                </div>
            </div>
        </Teleport>
    </PublicLayout>
</template>
